<!--
  Login. The console's front door: brand mark, one card, nothing else. Same
  radial-gradient canvas as the app behind it, so signing in feels like
  stepping through rather than switching apps.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Alert, Button, Card, Field, Input, Logo, Spinner } from '@navitech/ui';
	import { auth, HttpError } from '../lib/api';
	import { navigate } from '../lib/router';

	let email = $state('');
	let password = $state('');
	let pending = $state(false);
	let error = $state<string | null>(null);
	let ssoEnabled = $state(false);
	// Held until the silent attempt has been decided, so the form does not flash
	// on screen for somebody who is about to be redirected straight past it.
	let checking = $state(true);

	/**
	 * Suppresses the silent sign-in attempt.
	 *
	 * Per tab and timestamped: a logged-out operator must not be bounced through
	 * the provider on every refresh, but somebody who signs in at the provider in
	 * another tab should be picked up shortly after without clicking anything.
	 * Ten minutes is the same window the other Navitech applications use.
	 */
	const PROBE_KEY = 'sm:sso-probe';
	const PROBE_SUPPRESSION_MS = 10 * 60_000;

	function probedRecently(): boolean {
		try {
			return Date.now() - Number(sessionStorage.getItem(PROBE_KEY) ?? 0) < PROBE_SUPPRESSION_MS;
		} catch {
			// Private mode or blocked storage: probing once per load is still
			// better than never, and the provider answers immediately either way.
			return false;
		}
	}

	function markProbed() {
		try {
			sessionStorage.setItem(PROBE_KEY, String(Date.now()));
		} catch {
			/* nothing to remember it in */
		}
	}

	onMount(async () => {
		// A sign-in that failed at the IdP comes back as /?sso_error=… — read it
		// once, then take it out of the address bar so a refresh does not
		// re-show a message about something that is over.
		const params = new URLSearchParams(window.location.search);
		const failed = params.get('sso_error');
		if (failed) {
			error = failed;
			params.delete('sso_error');
			const rest = params.toString();
			window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
		}

		// `?sso=none` is the provider answering a silent attempt with "nobody is
		// signed in here". Nothing to show; just stop probing.
		if (params.get('sso') === 'none') {
			markProbed();
			params.delete('sso');
			const rest = params.toString();
			window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
		}

		ssoEnabled = (await auth.ssoStatus()).enabled;

		// Navitech ID is how this dashboard signs people in, so go there.
		// Interactively, not `prompt=none`: there is no password form left to
		// fall back to, so there is nothing to be silent for. Somebody already
		// signed in to another Navitech application comes straight back with a
		// session and never sees this page.
		//
		// `markProbed` before leaving is the loop breaker: a round trip that
		// comes back without a session shows this page — a button and any
		// error — instead of bouncing again.
		if (ssoEnabled && !probedRecently()) {
			markProbed();
			auth.ssoLogin('/');
			return;
		}

		checking = false;
	});

	async function submit(e: Event) {
		e.preventDefault();
		pending = true;
		error = null;
		try {
			await auth.login(email, password);
			navigate('/');
		} catch (err) {
			error = err instanceof HttpError ? err.message : 'Login failed';
		} finally {
			pending = false;
		}
	}
</script>

<div class="flex min-h-screen items-center justify-center p-6">
	{#if checking}
		<!-- The silent attempt is a redirect away. Showing the form first would
		     flash a sign-in screen at somebody who never needed to see it. -->
		<Spinner label="Signing in" />
	{:else}
		<Card class="w-full max-w-sm p-7 shadow-lg">
			<form onsubmit={submit}>
				<!-- The mark on its own, not the Logo lockup: the product name has to
				     stay the page's h1. -->
				<div class="mb-6 flex items-center gap-2.5">
					<Logo />
					<div class="leading-tight">
						<h1 class="text-sm font-bold tracking-[0.2px] text-text">Firewall Manager</h1>
						<p class="text-[10.5px] tracking-[0.4px] text-text-faint uppercase">Navitech · Phase 1</p>
					</div>
				</div>

				<p class="mb-5 text-[13px] text-text-dim">Sign in to the operator console.</p>

				{#if error}
					<Alert tone="err" class="mb-4">{error}</Alert>
				{/if}

				{#if ssoEnabled}
					<!--
						The only way in. The email + password path is refused by the
						backend while Navitech ID is configured: keeping it would mean an
						account disabled at the provider could still sign in here, and a
						passkey requirement there would mean nothing.
					-->
					<Button variant="primary" block onclick={() => auth.ssoLogin('/')}>
						Continue with Navitech ID
					</Button>

					<p class="mt-4 text-center text-xs text-text-dim">
						One account for Hosted, mail, storage and this dashboard.
					</p>
				{:else}
					<Field label="Email">
						<Input type="email" autocomplete="username" bind:value={email} required />
					</Field>

					<Field label="Password" class="mb-6">
						<Input type="password" autocomplete="current-password" bind:value={password} required />
					</Field>

					<Button type="submit" variant="primary" block loading={pending}>
						{pending ? 'Signing in…' : 'Sign in'}
					</Button>
				{/if}
			</form>
		</Card>
	{/if}
</div>
